import React from 'react'

import { Box, Tooltip } from '@mui/material'

import { APP_BUILD_DATE, APP_BUILD_TIME, APP_COMMIT_SHORT, APP_VERSION } from '../../config/version'
import date, { dateTimeFormats } from '../../functions/Date/date.functions'
import styles from './VersionBadge.styles'
import { VersionBadgeProps } from './VersionBadge.types'

const JAMAICA_ZONE = 'America/Jamaica'
const BUILD_TIME_FORMAT = `${dateTimeFormats.date.medium}, ${dateTimeFormats.time.short} ZZZZ`

const formatBuildDate = (raw: string): string | null => {
  if (!raw) return null

  const hasTime = /\dT\d/.test(raw)
  const parsed = hasTime ? date(raw) : date(raw, dateTimeFormats.date.api)

  if (!parsed.isValid) return null

  if (hasTime) {
    return parsed.dateTime.setZone(JAMAICA_ZONE).toFormat(dateTimeFormats.date.medium)
  }

  return parsed.format(dateTimeFormats.date.medium)
}

const formatBuildTimestamp = (raw: string): string | null => {
  if (!raw) return null

  const parsed = date(raw)

  if (!parsed.isValid) return null

  return parsed.dateTime.setZone(JAMAICA_ZONE).toFormat(BUILD_TIME_FORMAT)
}

const VersionBadge: React.FC<VersionBadgeProps> = ({ variant = 'footer' }) => {
  if (!APP_VERSION) return null

  const formattedBuildTime = formatBuildTimestamp(APP_BUILD_TIME)
  const formattedBuildDate = formatBuildDate(APP_BUILD_DATE) ?? formatBuildDate(APP_BUILD_TIME)

  const datePart = formattedBuildDate ? ` · ${formattedBuildDate}` : ''
  const label = `v${APP_VERSION}${datePart}`

  const tooltipParts: string[] = []

  if (APP_COMMIT_SHORT && APP_COMMIT_SHORT !== 'unknown') tooltipParts.push(`commit ${APP_COMMIT_SHORT}`)
  if (formattedBuildTime) tooltipParts.push(`built ${formattedBuildTime}`)
  else if (formattedBuildDate) tooltipParts.push(`built ${formattedBuildDate}`)

  const tooltip = tooltipParts.join(' · ') || 'Build information unavailable'

  if (variant === 'overlay') {
    const detailParts: string[] = []

    if (APP_COMMIT_SHORT && APP_COMMIT_SHORT !== 'unknown') detailParts.push(`commit ${APP_COMMIT_SHORT}`)
    if (formattedBuildTime) detailParts.push(`built ${formattedBuildTime}`)
    else if (formattedBuildDate) detailParts.push(`built ${formattedBuildDate}`)
    const details = detailParts.join(' · ')
    const fullLabel = details ? `v${APP_VERSION} · ${details}` : `v${APP_VERSION}`

    return (
      <Box component="span" role="status" aria-label={`App version ${fullLabel}`} title={fullLabel} sx={styles.overlay}>
        <Box component="span" sx={styles.overlayVersion}>{`v${APP_VERSION}`}</Box>

        {details && (
          <Box component="span" className="version-details" sx={styles.overlayDetails}>
            <Box component="span" sx={styles.overlayDetailsInner}>{` · ${details}`}</Box>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box component="span" role="status" aria-label={`App version ${label}`} sx={styles.footer}>
        {label}
      </Box>
    </Tooltip>
  )
}

export default VersionBadge
